<template>
    <BlockUI :blocked="loading">
        <Fluid>
            <h2>ClickUp Integration</h2>
            <p class="description">
                Connect your ClickUp workspace to enhance link previews and view
                task details directly within MyStekz.
            </p>
            <div class="oauth-container">
                <div class="oauth-action">
                    <template v-if="isSetup && hasOAuth2">
                        <div class="connected-status">
                            <i class="pi pi-check-circle"></i>
                            <span>Connected to ClickUp</span>
                        </div>
                        <Button
                            label="Reconnect"
                            icon="pi pi-refresh"
                            severity="secondary"
                            @click="connect"
                            :disabled="loading"
                            class="mt-3"
                        />
                    </template>
                    <template v-else>
                        <Button
                            label="Connect with ClickUp"
                            icon="pi pi-external-link"
                            severity="primary"
                            @click="connect"
                            :disabled="loading"
                            size="large"
                        />
                        <small class="help-text">
                            You'll be redirected to ClickUp to authorize this
                            connection.
                        </small>
                    </template>
                </div>
            </div>
        </Fluid>

        <div v-if="error" class="error-message">
            <i class="pi pi-exclamation-triangle"></i>
            {{ error }}
        </div>

        <div class="buttons">
            <Button
                v-if="isSetup"
                label="Delete Integration"
                icon="pi pi-trash"
                severity="danger"
                @click="confirmDelete"
                :disabled="loading"
                text
            />
            <div class="action-buttons">
                <Button
                    label="Close"
                    text
                    severity="secondary"
                    @click="close"
                    :disabled="loading"
                />
            </div>
        </div>
    </BlockUI>
</template>

<script setup lang="ts">
import {
    IntegrationType,
    useIntegrationManager,
    useIntegrationStore,
} from "@/stores/integration";
import BlockUI from "primevue/blockui";
import Button from "primevue/button";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import Fluid from "primevue/fluid";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { computed, inject, onMounted, Ref, ref } from "vue";

const toast = useToast();
const confirm = useConfirm();
const { connect, fetchSettings, deleteSettings } = useIntegrationStore(
    IntegrationType.CLICKUP
);
const { isSetup, integration } = useIntegrationManager(IntegrationType.CLICKUP);
const dialogRef = inject<Ref<DynamicDialogInstance>>("dialogRef");

const loading = ref(false);
const submitted = ref(false);
const error = ref("");

// OAuth2 information
const hasOAuth2 = computed(() => {
    if (!integration.value) return false;
    return Boolean(integration.value.integration_method === "oauth2");
});

const loadSettings = async () => {
    try {
        loading.value = true;
        error.value = "";
        await fetchSettings();
    } catch (err) {
        error.value = "Failed to load ClickUp settings";
        console.error("Error loading ClickUp settings:", err);
    } finally {
        loading.value = false;
    }
};

const confirmDelete = () => {
    confirm.require({
        message:
            "Are you sure you want to delete this ClickUp integration? This action cannot be undone.",
        header: "Confirm Deletion",
        icon: "pi pi-exclamation-triangle",
        acceptProps: {
            severity: "danger",
        },
        accept: async () => {
            try {
                loading.value = true;
                error.value = "";

                await deleteSettings();

                toast.add({
                    severity: "success",
                    summary: "Success",
                    detail: "ClickUp integration removed successfully",
                    life: 3000,
                });

                resetForm();
            } catch (err) {
                console.error("Error deleting ClickUp settings:", err);
                error.value = "Failed to remove integration. Please try again.";
            } finally {
                loading.value = false;
            }
        },
    });
};

const resetForm = () => {
    submitted.value = false;
    error.value = "";
};

const close = () => {
    if (dialogRef?.value) {
        dialogRef.value.close();
    }
};

onMounted(() => {
    loadSettings();
});
</script>

<style lang="scss" scoped>
h2 {
    margin-top: 0;
    margin-bottom: 0.5rem;
}

.description {
    color: #6c757d;
    margin-bottom: 1.5rem;
}

.field {
    padding-bottom: 1em;

    label {
        display: block;
        margin-bottom: 0.5em;
    }
}

.buttons {
    display: flex;
    justify-content: space-between;
    margin-top: 1.5rem;
    align-items: center;
    border-top: 1px solid #f0f0f0;
    padding-top: 1rem;
}

.action-buttons {
    display: flex;
    gap: 1em;
}

.help-text {
    color: #6c757d;
    display: block;
    margin-top: 5px;
    font-size: 0.85rem;
}

.required {
    color: #f44336;
}

.error-message {
    background-color: #fff3f3;
    color: #f44336;
    padding: 10px;
    border-radius: 4px;
    margin-top: 1rem;
    display: flex;
    align-items: center;
    gap: 8px;
}

.oauth-container {
    display: flex;
    gap: 2rem;
    padding: 1rem 0;

    .oauth-description {
        flex: 3;

        ul {
            padding-left: 1.5rem;
            margin-top: 0.5rem;
            color: #6c757d;

            li {
                margin-bottom: 0.5rem;
            }
        }
    }

    .oauth-action {
        flex: 2;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 1.5rem;
        background-color: #f8f9fa;
        border-radius: 8px;
        text-align: center;
    }
}

.connected-status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
    font-weight: 500;
    color: #4caf50;

    i {
        font-size: 1.2rem;
    }
}

.api-key-actions {
    margin-top: 1.5rem;
}

.mt-3 {
    margin-top: 1rem;
}
</style>
